import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { usePolling } from '../lib/usePolling';
import AddressFormDialog from './AddressFormDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Trash2, MapPin, Truck, CreditCard, ChevronRight, CheckCircle2, Loader2, Square, CheckSquare, ExternalLink, Lock } from 'lucide-react';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price) => `Rp ${Number(price).toLocaleString('id-ID')}`;

export default function CartPage() {
  const navigate = useNavigate();
  const { fetchCartCount, fetchOrdersCount, isAuthenticated, loading: authLoading } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({ full_name: '', phone: '', address_line: '', city: '', province: '', postal_code: '', country: 'Indonesia', latitude: null, longitude: null, subdistrict_id: null, subdistrict_label: '' });
  const [shipping, setShipping] = useState(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingChecked, setShippingChecked] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [paying, setPaying] = useState(false);
  // Duitku covers all payment methods (e-wallet, QRIS, VA) on its hosted page —
  // we no longer pre-pick on our side. Kept as a const so downstream code that
  // reads `paymentMethod` continues to work; backend's _resolve_payment_provider
  // maps 'va' → 'duitku'.
  const paymentMethod = 'va';
  // One-off delivery address: when buyer is shipping to someone else (e.g., a gift)
  // they can edit the cart's delivery address without overwriting their profile default.
  const [saveToProfile, setSaveToProfile] = useState(true);
  const [addressIsOneOff, setAddressIsOneOff] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successOrderId, setSuccessOrderId] = useState('');

  // Auth guard: checkout route requires login. Redirect logged-out users to home.
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) navigate('/', { replace: true });
  }, [authLoading, isAuthenticated, navigate]);

  // Handle ?payment=cancelled from Duitku
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'cancelled') {
      toast.info('Payment cancelled. Your order is still pending — you can retry from Orders.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    // If user navigated back from payment gateway, redirect to orders
    const paymentRedirect = sessionStorage.getItem('payment_redirect');
    if (paymentRedirect) {
      sessionStorage.removeItem('payment_redirect');
      navigate('/orders', { replace: true });
      return;
    }
  }, [navigate]);

  const fetchCart = useCallback(() => Promise.all([
    axios.get(`${API}/cart`),
    axios.get(`${API}/address/me`).catch(() => ({ data: null })),
  ]).then(([cartRes, addrRes]) => {
    setCart(cartRes.data);
    setSelected(new Set(cartRes.data.map(item => item.cart_id)));
    if (addrRes.data?.full_name) {
      // Only set address state on initial load — polling must NOT clobber the
      // user's in-progress address edits in the dialog (Feb 2026 bug fix).
      setAddress(prev => prev || addrRes.data);
      setAddressForm(prev => (prev && prev.full_name) ? prev : addrRes.data);
    }
  }).finally(() => setLoading(false)), []);
  usePolling(fetchCart, 20000);

  const removeItem = async (cartId) => {
    try {
      await axios.delete(`${API}/cart/${cartId}`);
      setCart(c => c.filter(i => i.cart_id !== cartId));
      setSelected(prev => { const n = new Set(prev); n.delete(cartId); return n; });
      fetchCartCount();
      toast.success('Removed from cart');
    } catch { toast.error('Failed to remove'); }
  };

  // Cart can hold items from many sellers, but a single checkout is restricted
  // to ONE seller (each seller has their own pickup origin / courier booking).
  // Group cart by seller so the UI can show separate "shop sections" and
  // restrict selection to one seller at a time.
  const sellerGroups = React.useMemo(() => {
    const groups = new Map();
    for (const item of cart) {
      const key = item.seller_id || 'unknown';
      if (!groups.has(key)) {
        groups.set(key, {
          seller_id: key,
          seller_name: item.seller_name || 'Seller',
          seller_subdistrict_id: item.seller_subdistrict_id,
          items: [],
        });
      }
      groups.get(key).items.push(item);
    }
    return Array.from(groups.values());
  }, [cart]);

  // Currently-selected seller (derived from selected cart_ids). null when nothing selected.
  const selectedSellerId = React.useMemo(() => {
    for (const item of cart) {
      if (selected.has(item.cart_id)) return item.seller_id;
    }
    return null;
  }, [cart, selected]);

  const resetShippingState = () => {
    setShipping('');
    setShippingOptions([]);
    setShippingChecked(false);
  };

  const toggleSelect = (cartId) => {
    const item = cart.find(c => c.cart_id === cartId);
    if (!item) return;
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(cartId)) {
        n.delete(cartId);
      } else {
        // If switching to a different seller, clear previous selections
        if (selectedSellerId && selectedSellerId !== item.seller_id) {
          n.clear();
          resetShippingState();
          toast.info(`Switched to ${item.seller_name}'s items. Items from other sellers must be checked out separately.`);
        }
        n.add(cartId);
      }
      return n;
    });
  };

  const toggleSelectSellerGroup = (sellerId) => {
    const group = sellerGroups.find(g => g.seller_id === sellerId);
    if (!group) return;
    const groupIds = group.items.map(i => i.cart_id);
    const allSelected = groupIds.every(id => selected.has(id));
    setSelected(prev => {
      const n = new Set(prev);
      if (allSelected) {
        // Deselect this group entirely
        groupIds.forEach(id => n.delete(id));
      } else {
        // Switching seller — clear previous selections + reset shipping
        if (selectedSellerId && selectedSellerId !== sellerId) {
          n.clear();
          resetShippingState();
        }
        groupIds.forEach(id => n.add(id));
      }
      return n;
    });
  };

  const saveAddress = async (e) => {
    e.preventDefault();
    try {
      if (saveToProfile) {
        // Persist to profile (existing behaviour) — affects future orders too.
        const res = await axios.put(`${API}/address/me`, addressForm);
        setAddress(res.data);
        setAddressIsOneOff(false);
        toast.success('Address saved to your profile');
      } else {
        // One-off: don't touch /address/me. Use the typed address only for this order.
        setAddress({ ...addressForm });
        setAddressIsOneOff(true);
        toast.success('One-time delivery address set for this order');
      }
      // Force shipping rate re-fetch since destination kecamatan may have changed
      resetShippingState();
      setShowAddressForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save address');
    }
  };

  const shippingOption = shippingOptions.find(s => s.id === shipping);
  const selectedItems = cart.filter(item => selected.has(item.cart_id));
  const subtotal = selectedItems.reduce((sum, item) => sum + (item.listing?.price || 0) * item.quantity, 0);
  const shippingCost = shippingOption ? shippingOption.cost : 0;
  const total = subtotal + (selectedItems.length > 0 ? shippingCost : 0);
  const totalItems = selectedItems.reduce((sum, item) => sum + item.quantity, 0);

  // Get unique seller subdistrict IDs from selected items
  const getSellerSubdistrictId = () => {
    for (const item of selectedItems) {
      if (item.seller_subdistrict_id) return item.seller_subdistrict_id;
    }
    return null;
  };

  const checkShippingCost = async () => {
    if (!address?.subdistrict_id) {
      toast.error('Please add a delivery address with subdistrict first');
      return;
    }
    const sellerOrigin = getSellerSubdistrictId();
    if (!sellerOrigin) {
      toast.error('Seller location not available for shipping estimate');
      return;
    }
    setShippingLoading(true);
    try {
      const res = await axios.post(`${API}/shipping/cost`, {
        origin: sellerOrigin,
        destination: address.subdistrict_id,
        weight: 1000,
        couriers: 'jne:jnt:sicepat',
      });
      const options = (res.data || []).map((r, i) => ({
        id: `${r.code}_${r.service}_${i}`,
        name: r.name,
        service: r.service,
        description: r.description,
        cost: r.cost,
        etd: r.etd || '-',
      }));
      setShippingOptions(options);
      setShippingChecked(true);
      // Auto-select the cheapest option
      if (options.length > 0) {
        const cheapest = options.reduce((min, o) => o.cost < min.cost ? o : min, options[0]);
        setShipping(cheapest.id);
      }
    } catch {
      toast.error('Failed to get shipping rates. Try again.');
    } finally {
      setShippingLoading(false);
    }
  };

  const handlePay = async () => {
    if (!address) { toast.error('Please add a delivery address'); return; }
    if (selectedItems.length === 0) { toast.error('Please select at least one item to pay for'); return; }
    if (!shipping) { toast.error('Please select a shipping courier'); return; }

    setPaying(true);
    try {
      const selectedShipping = shippingOptions.find(s => s.id === shipping);
      const res = await axios.post(`${API}/orders/create-from-cart`, {
        cart_ids: Array.from(selected),
        shipping_cost: selectedShipping?.cost || 0,
        courier_name: selectedShipping ? `${selectedShipping.name} ${selectedShipping.service}` : '',
        payment_method: paymentMethod,
        // Send one-off delivery override if the buyer chose not to save to profile
        ...(addressIsOneOff && address ? { delivery_address: address } : {}),
      });
      const { payment_url, payment_provider, qrisly_history_id, qris_error, order_id } = res.data;

      // Clear cart UI for ordered items regardless of provider
      const paidIds = new Set(selected);
      setCart(prev => prev.filter(item => !paidIds.has(item.cart_id)));
      setSelected(new Set());
      fetchCartCount();
      fetchOrdersCount();

      if (payment_provider === 'qrisly') {
        if (!qrisly_history_id) {
          toast.error(qris_error || 'Unable to generate QR. Please try again.');
          setPaying(false);
          return;
        }
        toast.info('Opening QRIS checkout…');
        navigate(`/payment/qris?order=${order_id}`, { replace: true });
      } else if (payment_url) {
        // Legacy Duitku: hand off to interstitial redirect page.
        sessionStorage.setItem('payment_redirect', order_id);
        toast.info('Redirecting to payment page...');
        navigate(`/payment/redirect?order=${order_id}`, { replace: true });
      } else {
        // Fallback: provider didn't return a payment URL — surface the error
        // and keep the order in 'pending' so buyer can retry. (Mock Pay is
        // admin-only now; buyers can't self-bypass.)
        toast.error('Payment provider temporarily unavailable. Please try again in a moment.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'multi_seller_cart') {
        toast.error(detail.message || 'Items from different sellers must be checked out separately.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create order');
      }
      setPaying(false);
    }
  };

  const handlePaymentComplete = (order_id) => {
    const paidIds = new Set(selected);
    setCart(prev => prev.filter(item => !paidIds.has(item.cart_id)));
    setSelected(new Set());
    fetchCartCount();
    fetchOrdersCount();
    setSuccessOrderId(order_id);
    setShowSuccess(true);
    setPaying(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFAF8]"><Header />
        <div className="max-w-3xl mx-auto px-4 py-8 animate-pulse">
          <div className="h-8 bg-cream-100 rounded w-32 mb-6" />
          <div className="space-y-4">{[1,2].map(i => <div key={i} className="h-24 bg-cream-100 rounded-2xl" />)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl text-[#2C2A29] mb-6" data-testid="cart-title">Your Cart</h1>

        {cart.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-cream-100/50" data-testid="empty-cart">
            <p className="font-body text-[#78716C] mb-4">Your cart is empty</p>
            <button onClick={() => navigate('/')} className="bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-2.5 font-body font-semibold hover:bg-[#ECAEAE] transition-all">
              Browse Products
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Cart Items — single-seller cart (multi-seller temporarily restricted
                at /cart/add. The seller-grouping code below still works if a
                user somehow has multiple sellers from a legacy state — they'll
                see the friendly hint and can checkout one shop at a time. The
                current beta enforces single-seller carts at the source). */}
            <div className="space-y-3" data-testid="cart-items">
              {/* Legacy multi-seller hint — only shown if a user happens to have
                  items from >1 seller (shouldn't happen post-restriction). */}
              {sellerGroups.length > 1 && (
                <div className="bg-cream-50 rounded-2xl px-4 py-3 border border-cream-100/60 text-xs font-body text-[#78716C] leading-relaxed" data-testid="multi-seller-hint">
                  <span className="font-semibold text-[#2C2A29]">Multiple sellers in your cart.</span> Each seller ships separately, so you'll check out one shop at a time. Select items from one seller to continue.
                </div>
              )}

              {/* Simple "Select All" — works correctly when there's exactly one
                  seller group (the common case). */}
              {sellerGroups.length === 1 && cart.length > 0 && (
                <div className="flex items-center gap-3 px-1 mb-1">
                  <button onClick={() => toggleSelectSellerGroup(sellerGroups[0].seller_id)}
                    className="flex items-center gap-2 group" data-testid="select-all-btn">
                    {selected.size === cart.length ? (
                      <CheckSquare size={20} className="text-[#F4C2C2]" />
                    ) : (
                      <Square size={20} className="text-[#A89F91] group-hover:text-[#F4C2C2] transition-colors" />
                    )}
                    <span className="text-sm font-body font-medium text-[#78716C]">
                      {selected.size === cart.length ? 'Deselect All' : 'Select All'}
                    </span>
                  </button>
                  <span className="text-xs font-body text-[#A89F91]">
                    {selected.size} of {cart.length} selected · Sold by <span className="font-semibold text-[#2C2A29]">{sellerGroups[0].seller_name}</span>
                  </span>
                </div>
              )}

              {sellerGroups.map(group => {
                const groupSelected = group.items.every(i => selected.has(i.cart_id));
                const someSelected = group.items.some(i => selected.has(i.cart_id));
                const isOtherSellerActive = selectedSellerId && selectedSellerId !== group.seller_id;
                // For the single-seller case, render a flat list (no per-seller header).
                // For the multi-seller case (legacy), keep the grouped UI.
                if (sellerGroups.length === 1) {
                  return (
                    <div key={group.seller_id} className="space-y-2">
                      {group.items.map(item => (
                        <div key={item.cart_id} className={`bg-white rounded-2xl p-4 border flex gap-4 transition-all ${
                          selected.has(item.cart_id) ? 'border-[#F4C2C2]/60 shadow-[0_0_0_1px_rgba(244,194,194,0.2)]' : 'border-cream-100/50'
                        }`} data-testid={`cart-item-${item.cart_id}`}>
                          <button onClick={() => toggleSelect(item.cart_id)} className="flex-shrink-0 self-center" data-testid={`cart-check-${item.cart_id}`}>
                            {selected.has(item.cart_id) ? (
                              <CheckSquare size={22} className="text-[#F4C2C2]" />
                            ) : (
                              <Square size={22} className="text-[#A89F91] hover:text-[#F4C2C2] transition-colors" />
                            )}
                          </button>
                          <img src={item.listing?.photos?.[0] || 'https://via.placeholder.com/100'} alt=""
                            className="w-20 h-20 rounded-xl object-cover flex-shrink-0 cursor-pointer"
                            onClick={() => navigate(`/listing/${item.listing_id}`)} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-body uppercase tracking-[0.15em] text-[#A89F91]">{item.listing?.brand}</p>
                            <p className="font-body font-semibold text-[#2C2A29] text-sm truncate">{item.listing?.product_name}</p>
                            <p className="text-xs font-body text-[#78716C]">Sold by {item.seller_name}</p>
                            <div className="flex items-center justify-between mt-2">
                              <p className="font-heading font-semibold text-[#2C2A29]">{formatPrice(item.listing?.price)} <span className="text-xs font-body text-[#A89F91]">x{item.quantity}</span></p>
                              <button onClick={() => removeItem(item.cart_id)} className="text-[#A89F91] hover:text-red-400 transition-colors" data-testid={`remove-cart-${item.cart_id}`}>
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }
                // ── Multi-seller fallback (legacy / future) — keeps the
                // grouped Shopee-style UI intact behind sellerGroups.length > 1. ──
                return (
                  <div key={group.seller_id} className="bg-cream-50/40 rounded-2xl p-3 border border-cream-100/40">
                    <div className="flex items-center justify-between gap-2 px-1 pb-2">
                      <button onClick={() => toggleSelectSellerGroup(group.seller_id)}
                        className="flex items-center gap-2 group min-w-0"
                        data-testid={`select-seller-${group.seller_id}`}>
                        {groupSelected ? (
                          <CheckSquare size={18} className="text-[#F4C2C2] flex-shrink-0" />
                        ) : someSelected ? (
                          <Square size={18} className="text-[#F4C2C2] flex-shrink-0" />
                        ) : (
                          <Square size={18} className="text-[#A89F91] group-hover:text-[#F4C2C2] transition-colors flex-shrink-0" />
                        )}
                        <span className="text-sm font-body font-semibold text-[#2C2A29] truncate">{group.seller_name}</span>
                        <span className="text-[10px] font-body text-[#A89F91]">· {group.items.length} item{group.items.length === 1 ? '' : 's'}</span>
                      </button>
                      {isOtherSellerActive && (
                        <span className="text-xs font-body text-[#A89F91] italic flex-shrink-0">checkout other seller first</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {group.items.map(item => (
                        <div key={item.cart_id} className={`bg-white rounded-2xl p-4 border flex gap-4 transition-all ${
                          selected.has(item.cart_id) ? 'border-[#F4C2C2]/60 shadow-[0_0_0_1px_rgba(244,194,194,0.2)]' : 'border-cream-100/50'
                        } ${isOtherSellerActive ? 'opacity-50' : ''}`} data-testid={`cart-item-${item.cart_id}`}>
                          <button onClick={() => toggleSelect(item.cart_id)} className="flex-shrink-0 self-center" data-testid={`cart-check-${item.cart_id}`}>
                            {selected.has(item.cart_id) ? (
                              <CheckSquare size={22} className="text-[#F4C2C2]" />
                            ) : (
                              <Square size={22} className="text-[#A89F91] hover:text-[#F4C2C2] transition-colors" />
                            )}
                          </button>
                          <img src={item.listing?.photos?.[0] || 'https://via.placeholder.com/100'} alt=""
                            className="w-20 h-20 rounded-xl object-cover flex-shrink-0 cursor-pointer"
                            onClick={() => navigate(`/listing/${item.listing_id}`)} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-body uppercase tracking-[0.15em] text-[#A89F91]">{item.listing?.brand}</p>
                            <p className="font-body font-semibold text-[#2C2A29] text-sm truncate">{item.listing?.product_name}</p>
                            <div className="flex items-center justify-between mt-2">
                              <p className="font-heading font-semibold text-[#2C2A29]">{formatPrice(item.listing?.price)} <span className="text-xs font-body text-[#A89F91]">x{item.quantity}</span></p>
                              <button onClick={() => removeItem(item.cart_id)} className="text-[#A89F91] hover:text-red-400 transition-colors" data-testid={`remove-cart-${item.cart_id}`}>
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Address */}
            <div className="bg-white rounded-2xl p-4 border border-cream-100/50" data-testid="address-section">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-[#C5A880]" />
                  <span className="text-sm font-body font-medium text-[#78716C]">Delivery Address</span>
                  {addressIsOneOff && (
                    <span className="text-[10px] font-body bg-[#9CAF88]/15 text-[#5d6f49] rounded-full px-2 py-0.5" data-testid="address-one-off-badge">One-time</span>
                  )}
                </div>
                <button onClick={() => { setSaveToProfile(!addressIsOneOff); setShowAddressForm(true); }} className="text-xs font-body text-[#C5A880] font-semibold hover:underline" data-testid="edit-address-btn">
                  {address ? 'Edit' : 'Add Address'}
                </button>
              </div>
              {address ? (
                <div className="text-sm font-body text-[#2C2A29]">
                  <p className="font-semibold">{address.full_name}</p>
                  <p className="text-[#78716C]">{address.phone}</p>
                  <p className="text-[#78716C]">{address.address_line}{address.city ? `, ${address.city}` : ''}{address.province ? `, ${address.province}` : ''}</p>
                  {address.subdistrict_label && (
                    <p className="text-[#78716C] text-xs mt-0.5">📍 {address.subdistrict_label}</p>
                  )}
                  {address.latitude && address.longitude && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <MapPin size={10} /> Location pinned
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm font-body text-[#A89F91] italic">No address saved yet</p>
              )}
            </div>

            {/* Address Form Dialog */}
            <AddressFormDialog
              open={showAddressForm}
              onOpenChange={setShowAddressForm}
              addressForm={addressForm}
              setAddressForm={setAddressForm}
              onSave={saveAddress}
              showSaveToProfile={true}
              saveToProfile={saveToProfile}
              setSaveToProfile={setSaveToProfile}
            />

            {/* Shipping */}
            <div className="bg-white rounded-2xl p-4 border border-cream-100/50" data-testid="shipping-section">
              <div className="flex items-center gap-2 mb-3">
                <Truck size={16} className="text-[#C5A880]" />
                <span className="text-sm font-body font-medium text-[#78716C]">Shipment Options</span>
              </div>

              {!shippingChecked ? (
                <div className="space-y-3">
                  <p className="text-xs font-body text-[#A89F91]">
                    Check real-time shipping rates from JNE, J&T, and SiCepat based on your delivery address.
                  </p>
                  <button
                    type="button"
                    onClick={checkShippingCost}
                    disabled={shippingLoading || !address?.subdistrict_id}
                    className="flex items-center gap-2 text-sm font-body font-semibold text-[#C5A880] hover:text-[#A68A5B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="check-shipping-btn"
                  >
                    {shippingLoading ? <><Loader2 size={14} className="animate-spin" /> Checking rates...</> : <><ExternalLink size={14} /> Check shipment fee estimation</>}
                  </button>
                  {!address?.subdistrict_id && address && (
                    <p className="text-xs font-body text-amber-600">Please update your address with a subdistrict to check shipping rates.</p>
                  )}
                </div>
              ) : shippingOptions.length === 0 ? (
                <div className="text-center py-3">
                  <p className="text-sm font-body text-[#A89F91]">No shipping options available for this route.</p>
                  <button onClick={checkShippingCost} className="text-xs font-body text-[#C5A880] font-semibold mt-1 hover:underline">Retry</button>
                </div>
              ) : (
                <div className="space-y-2">
                  {shippingOptions.map(opt => (
                    <label key={opt.id} className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all ${
                      shipping === opt.id ? 'border-[#F4C2C2] bg-[#FFF5F5]' : 'border-cream-100 hover:border-[#F4C2C2]'
                    }`} data-testid={`shipping-${opt.id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${shipping === opt.id ? 'border-[#F4C2C2]' : 'border-cream-200'}`}>
                          {shipping === opt.id && <div className="w-2 h-2 rounded-full bg-[#F4C2C2]" />}
                        </div>
                        <div>
                          <p className="text-sm font-body font-medium text-[#2C2A29]">{opt.name} — {opt.service}</p>
                          <p className="text-xs font-body text-[#A89F91]">{opt.description} {opt.etd !== '-' ? `(${opt.etd})` : ''}</p>
                        </div>
                      </div>
                      <span className="text-sm font-body font-semibold text-[#2C2A29]">{formatPrice(opt.cost)}</span>
                      <input type="radio" name="shipping" value={opt.id} checked={shipping === opt.id}
                        onChange={() => setShipping(opt.id)} className="hidden" />
                    </label>
                  ))}
                  <button onClick={checkShippingCost} disabled={shippingLoading}
                    className="text-xs font-body text-[#C5A880] font-semibold hover:underline flex items-center gap-1 mt-1"
                    data-testid="refresh-shipping-btn">
                    {shippingLoading ? <><Loader2 size={12} className="animate-spin" /> Refreshing...</> : 'Refresh rates'}
                  </button>
                  {/* Manual-shipment disclaimer — sellers handle fulfillment themselves
                      and pick their preferred courier. Buyer's selection above is used
                      only to estimate shipping cost & ETA. */}
                  <p className="text-xs font-body text-[#78716C] bg-cream-50 border border-cream-100 rounded-xl px-3 py-2 mt-2 leading-relaxed" data-testid="shipping-manual-disclaimer">
                    Seller might ship using a different courier with the same ETA as your selected courier. Tracking number will be shared by them once shipped.
                  </p>
                </div>
              )}
            </div>

            {/* Payment Details */}
            <div className="bg-white rounded-2xl p-4 border border-cream-100/50" data-testid="payment-details">
              <p className="text-sm font-body font-medium text-[#78716C] mb-3">Details of Payment</p>
              <div className="space-y-2 text-sm font-body">
                <div className="flex justify-between"><span className="text-[#78716C]">Items ({totalItems})</span><span className="text-[#2C2A29]">{formatPrice(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-[#78716C]">Platform fee</span><span className="text-emerald-600 font-semibold">Rp 0 (Beta)</span></div>
                <div className="flex justify-between"><span className="text-[#78716C]">Shipping{shippingOption ? ` (${shippingOption.name} ${shippingOption.service})` : ''}</span><span className="text-[#2C2A29]">{shippingCost > 0 ? formatPrice(shippingCost) : shippingChecked ? 'Select courier' : 'Check rates'}</span></div>
                <div className="border-t border-cream-100 pt-2 flex justify-between">
                  <span className="font-semibold text-[#2C2A29]">Total</span>
                  <span className="font-heading text-lg font-semibold text-[#2C2A29]">{formatPrice(total)}</span>
                </div>
              </div>
            </div>

            {/* Payment method info — Duitku covers e-wallet, QRIS, and VA.
                Buyer picks the exact method on Duitku's hosted page after Pay. */}
            <div className="bg-white rounded-2xl p-4 border border-cream-100/50" data-testid="payment-method-info">
              <p className="text-sm font-body font-medium text-[#78716C] mb-2">Payment Methods</p>
              <p className="font-body text-sm text-[#2C2A29] leading-relaxed">
                Pay with <span className="font-semibold">e-wallet</span>, <span className="font-semibold">QRIS</span>, <span className="font-semibold">Virtual Account</span>, or via <span className="font-semibold">Retail</span>.
              </p>
              <p className="text-xs font-body text-[#A89F91] mt-2 leading-relaxed">
                You'll choose your exact method on the next step.
              </p>
            </div>

            {/* Trust / security line — explains the gateway model */}
            <div className="flex items-center gap-2 px-1" data-testid="payment-trust-line">
              <Lock size={12} className="text-[#78716C] flex-shrink-0" />
              <p className="text-xs font-body text-[#78716C] leading-relaxed">
                Payment secured by <span className="font-semibold text-[#2C2A29]">Duitku</span>. {'{Beauty}'} Stashmart never sees or stores your card, bank, or wallet credentials.
              </p>
            </div>

            {/* Pay Button */}
            <button onClick={handlePay} disabled={selectedItems.length === 0 || !shipping || paying}
              className="w-full bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-4 font-body font-semibold text-lg transition-all hover:bg-[#ECAEAE] hover:shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="pay-btn">
              {paying ? <><Loader2 size={18} className="animate-spin" /> Processing...</> : selectedItems.length === 0 ? 'Select items to pay' : !shipping ? 'Select a courier first' : `Pay ${formatPrice(total)} (${totalItems} item${totalItems > 1 ? 's' : ''})`}
            </button>
          </div>
        )}
      </main>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={() => {}}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-sm mx-4 text-center" data-testid="order-success-modal">
          <div className="py-4">
            <CheckCircle2 size={56} className="mx-auto text-green-500 mb-4" />
            <h2 className="font-heading text-2xl text-[#2C2A29] mb-2">Payment Successful!</h2>
            <p className="font-body text-[#78716C] mb-1">Payment received! We've notified the seller to ship your order.</p>
            <p className="font-body text-xs text-[#A89F91] mb-6">Order ID: <span className="font-semibold">{successOrderId}</span></p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => { setShowSuccess(false); navigate('/orders'); }}
                className="bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-3 font-body font-semibold hover:bg-[#ECAEAE] transition-all"
                data-testid="go-to-orders-btn">
                View Orders
              </button>
              <button onClick={() => { setShowSuccess(false); navigate('/'); }}
                className="border border-cream-200 text-[#78716C] rounded-full px-6 py-3 font-body font-semibold hover:bg-cream-50 transition-all"
                data-testid="back-to-home-btn">
                Home
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
